import { useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import type { Location } from 'history';

interface UsePageExitOptions {
  onExit?: () => void;
  onBeforeUnload?: () => void;
  onVisibilityChange?: () => void;
  onRouteChange?: () => void;
  shouldPreventDefault?: boolean;
  shouldBlockRouting?: boolean;
  enabled?: boolean;
  customConfirmModal?: () => Promise<boolean>;
  exitPath?: string;
}

const usePageExit = (callback: () => void, options: UsePageExitOptions = {}) => {
  const {
    onExit,
    onBeforeUnload,
    onVisibilityChange,
    onRouteChange,
    shouldPreventDefault = false,
    shouldBlockRouting = false,
    enabled = true,
    customConfirmModal,
    exitPath,
  } = options;

  const history = useHistory();
  const callbackRef = useRef(callback);
  const isExecutedRef = useRef(false);
  const enabledRef = useRef(enabled);
  const isLeavingRef = useRef(false);

  callbackRef.current = callback;
  enabledRef.current = enabled;

  const executeCallback = useCallback(() => {
    if (!enabledRef.current || isExecutedRef.current) return;

    isExecutedRef.current = true;
    try {
      callbackRef.current();
      onExit?.();
    } catch (error) {
      console.error('페이지 이탈 콜백 실행 중 오류:', error);
    }

    setTimeout(() => {
      isExecutedRef.current = false;
    }, 100);
  }, [onExit]);

  useEffect(() => {
    if (!enabled) {
      return () => {
        // eslint-disable-next-line no-empty-function
      };
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      executeCallback();
      onBeforeUnload?.();

      if (shouldPreventDefault) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
        return '';
      }
      return undefined;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        executeCallback();
        onVisibilityChange?.();
      }
    };

    const handlePageHide = () => {
      executeCallback();
    };

    const handleUnload = () => {
      executeCallback();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('unload', handleUnload);
    };
  }, [enabled, executeCallback, onBeforeUnload, onVisibilityChange, shouldPreventDefault]);

  // 라우팅 감지 및 차단
  useEffect(() => {
    if (!enabled) {
      return () => {
        // eslint-disable-next-line no-empty-function
      };
    }

    let unblock: (() => void) | undefined;

    if (shouldBlockRouting) {
      // 라우팅 차단 모드
      const blocker = (location: Location, action: string): string | false | void => {
        if ((action === 'PUSH' || action === 'REPLACE' || action === 'POP') &&
          enabledRef.current && !isLeavingRef.current) {

          // 커스텀 모달이 있으면 사용, 없으면 기본 confirm 사용
          if (customConfirmModal) {
            customConfirmModal().then((shouldLeave) => {
              if (shouldLeave) {
                isLeavingRef.current = true;
                executeCallback();
                onRouteChange?.();

                // blocker 해제 후 실제 네비게이션 수행
                if (unblock) {
                  unblock();
                  unblock = undefined;
                }

                setTimeout(() => {
                  try {
                    if (exitPath) {
                      history.push(exitPath);
                    } else if (action === 'POP') {
                      history.goBack();
                    } else {
                      history.push(location.pathname);
                    }
                  } finally {
                    setTimeout(() => {
                      isLeavingRef.current = false;
                    }, 50);
                  }
                }, 10);
              }
            });
          } else {
            // 기본 confirm 사용
            const shouldLeave = window.confirm('페이지를 떠나시겠습니까? 변경사항이 저장되지 않을 수 있습니다.');

            if (shouldLeave) {
              isLeavingRef.current = true;
              executeCallback();
              onRouteChange?.();

              // blocker 해제 후 실제 네비게이션 수행
              if (unblock) {
                unblock();
                unblock = undefined;
              }

              setTimeout(() => {
                try {
                  if (exitPath) {
                    history.push(exitPath);
                  } else if (action === 'POP') {
                    history.goBack();
                  } else {
                    history.push(location.pathname);
                  }
                } finally {
                  setTimeout(() => {
                    isLeavingRef.current = false;
                  }, 50);
                }
              }, 10);
            }
          }

          return false; // 네비게이션 차단
        }

        return undefined;
      };

      try {
        const blockResult = history.block(blocker);
        if (typeof blockResult === 'function') {
          unblock = blockResult;
        }
      } catch (error) {
        console.error('라우팅 차단 설정 중 오류:', error);
      }
    } else {
      // 단순 감지 모드
      const unlisten = history.listen((location, action) => {
        if (action === 'PUSH' || action === 'REPLACE') {
          executeCallback();
          onRouteChange?.();
        }
      });

      return () => {
        unlisten();
      };
    }

    return () => {
      try {
        unblock?.();
      } catch (error) {
        console.error('라우팅 차단 해제 중 오류:', error);
      }
    };
  }, [enabled, shouldBlockRouting, history, executeCallback, onRouteChange, customConfirmModal, exitPath]);

  const reset = useCallback(() => {
    isExecutedRef.current = false;
    isLeavingRef.current = false;
  }, []);

  const trigger = useCallback(() => {
    executeCallback();
  }, [executeCallback]);

  const allowExit = useCallback(() => {
    isLeavingRef.current = true;
  }, []);

  return { reset, trigger, allowExit };
};

export default usePageExit;